import { isInMiniProgram } from '@/pages/chargermap/utils/acrossAgent'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { OS } from '@/utils/device'
import { nioJsBridge, universalStorage } from '@nio-wad/universal-bridge-h5'
import debounce from 'lodash.debounce'
import Tracker from '@nio-fe/nio-tracker-web'
import {
  defineComponent,
  h,
  inject,
  onUnmounted,
  reactive,
  ref,
  resolveComponent,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getChargemapCDNurl } from '../../apis/map.api'
import AutoRefresh from '../../components/autoRefresh'
import BackListRecord from '../../components/backListRecord'
import ChargerMap from '../../components/chargerMap'
import { ChargeMapContext } from '../../components/chargerMap/index.model'
import {
  calcSize,
  changeChargingStatus,
  fitWindows,
  flyTo,
} from '../../components/chargerMap/mapUtils'
import { FilterItem, FilterState } from '../../components/filter/filter.model'
import PopupDrag from '../../components/popupDrag'
import WsService from '../../components/wsService'
import { DEFAULT_POSITION } from '../../config/defaultPosition'
import specialArea from '../../config/specialArea.json'
import {
  DEFAULT_CITY_ZOOM_LEVEL,
  DEFAULT_COUNTRY_ZOOM_LEVEL,
  DEFAULT_DISTRICT_ZOOM_LEVEL,
} from '../../config/zoomLevelConfig'
import {
  BubbleDestPoint,
  CityInfo,
  Loc,
  MapViewMode,
  Marker,
  Point,
  ZoomMode,
} from '../../contract/map'
import { SearchPoint } from '../../contract/searchPoint'
import { RouteCDNModel } from '../../models/detail.model'
import { useAppStore } from '../../stores/app'
import { useComponentStore } from '../localStores/component'
import { setMapStyleByConfig } from './biz/utils'
import styles from './styles.module.scss'
import { isInApp } from '@nio-wad/hooks'

export type CityMapViewProp = {
  renderComps: (compList: any) => void
}

export default defineComponent({
  name: 'city-map-view',
  props: {
    xinfaView: {
      // 信发设备使用此组件,需要隐藏handleBox组件
      type: Boolean,
      default: false,
    },
  },
  emits: ['ready'],
  setup: (props, { expose, emit }) => {
    const client = inject<string>('client', '')
    const channel = inject<string>('channel', '')
    const xinfaView =
      props.xinfaView ||
      ((channel == 'store' || channel.indexOf('xinfa') > -1) && client == 'android')

    let mapContext: ChargeMapContext
    const mapContextRef = ref<ChargeMapContext>()
    const mapInstance = ref<any>()
    const BMapGL = ref<any>()

    const router = useRouter()
    const route = useRoute()
    const appStore = useAppStore()
    const componentStore = useComponentStore()

    let selectedCityLoc: Point | null

    // 获取组件配置
    const componentsConfig: any = window.NSComponentsConfig

    const state = reactive({
      mapSetting: {
        center: appStore.center, // 初始化中心点
        zoom: appStore.zoomLevel, // 初始化zoom
        minZoom: appStore.minZoomLevel,
        maxZoom: appStore.maxZoomLevel,
        infoZoomLevel: 12, // 合适看信息的zoom eg. 搜索，切换数据
      },
      viewMode: MapViewMode.Init, // TODO component.viewMode
      zoomMode: ZoomMode.Country,
    })

    // let thirdSourceIndex: number[] = [] // 三方点的位置
    // let resourceFilter: FilterItem[] = [] // 资源点

    const cdnUrlResProps = ref<RouteCDNModel>()

    const initCountyLayoutRedDot = async () => {
      if (componentsConfig.county_layout?.show && isInApp) {
        const result = await universalStorage.get({
          data: { key: 'viewedCountyLayout' },
        })
        if (!(result.data && result.data.key === 'viewedCountyLayout' && result.data.value)) {
          // 没有预览过，显示红点
          componentStore.$patch({
            showCountyLayoutRedDot: true,
          })
        }
      }
    }

    initCountyLayoutRedDot() // 初始化县县通小红点

    // click隐藏logo和数据概览面板
    const showHandlebox = () => {
      componentStore.$patch({
        showHandleBox: true,
      })
      componentStore.bus.emit('onChangeHeightLevel', 0) // dragPopup高度回到1
    }
    const hideHandlebox = () => {
      componentStore.$patch({
        showHandleBox: false,
      })
    }
    const showSummary = () => {
      componentStore.$patch({
        showSummary: true,
      })
      console.log('OS.isNioBrowser', OS.isNioBrowser)
      console.log('window.channel', window.channel)
      if (!OS.isNioBrowser) {
        // nio浏览器不可点击
        // 移动端在PopupDrag中触发
        document.addEventListener('touchend', hideSummary)
        document.addEventListener('click', hideSummary)
      }
      hideHandlebox()
    }
    const hideSummary = (e?: Event) => {
      if (!componentStore.showSummary) return

      console.log('==============e?.target?.className======================')
      console.log((e?.target as HTMLElement)?.className)
      console.log('====================================')

      if (e && (e.target as HTMLElement)?.className?.trim() === 'BMap_mask') {
        componentStore.$patch({ showSummary: false })
        document.removeEventListener('touchend', hideSummary)
        document.removeEventListener('click', hideSummary)
        showHandlebox()
      } else if (!e) {
        componentStore.$patch({ showSummary: false })
        document.removeEventListener('touchend', hideSummary)
        document.removeEventListener('click', hideSummary)
        showHandlebox()
      }
    }
    componentStore.bus.on('hideSummary', () => {
      hideSummary()
    })

    /**
     * filter的初始化，用于判断是否接收ws的实时换电数据
     */
    // componentStore.bus.on('initResourceFilter', (params: any) => {
    //   thirdSourceIndex = params.thirdSourceIndex
    //   resourceFilter =
    //     params.filter[thirdSourceIndex[0]]?.options[thirdSourceIndex[1]]?.options || []
    // })

    /**
     * filter的变化，用于判断是否接收ws的实时换电数据
     */
    // componentStore.bus.on('filterChanged', (params: any) => {
    //   thirdSourceIndex = params.thirdSourceIndex
    //   resourceFilter =
    //     params.filter[thirdSourceIndex[0]]?.options[thirdSourceIndex[1]]?.options || []
    // })

    // 点击搜索候选项
    componentStore.bus.on('searchPoiResource', async (loc: Loc | SearchPoint) => {
      componentStore.$patch({ showSearchPanel: false }) // 清除搜索态状态
      componentStore.bus.emit('clearLocationMarker') // 清除pin点，因为搜索模式下不能戳点
      componentStore.bus.emit('forceClearBackList') // 清空backList
      window.backHistoryNum = 1
      if (loc.pointType == 'poi') {
        const routeTarget = {
          name: 'pin',
          query: {
            // id: loc.id,
            location: loc.lng + ',' + loc.lat, // gcj
            name: loc.name || '',
            address: loc.address || '',
          },
          state: {
            backHistoryNum: window.backHistoryNum, // 后退只能回到首页
          },
        }
        await router.replace(routeTarget)
        // 从搜索列表点入的时候，点击返回按钮都是回到点击的点，所以需要记录backList
        componentStore.bus.emit('pushBackList', routeTarget)
        componentStore.bus.emit('selectedPin')
      } else {
        const routeTarget = {
          name: 'resource',
          query: {
            id: loc.id,
            location: loc.lng + ',' + loc.lat,
            pointType: loc.pointType,
            pointSubType: loc.pointSubType,
          },
          state: {
            backHistoryNum: window.backHistoryNum, // 后退只能回到首页
          },
        }
        const marker = mapContextRef.value?.getMarkerById(loc.id)
        if (marker) {
          // 已经画出了点
          await router.push(routeTarget)
        } else {
          await router.replace(routeTarget)
        }
        // ! onClickedMarker 里面会pushBackList
        // 从搜索列表点入的时候，点击返回按钮都是回到点击的点，所以需要记录backList
        componentStore.bus.emit('selectedMarker')
      }
      // 搜索点击埋点
      Tracker.track(
        'nsh5_citymapclicksearchword',
        Object.assign(appStore.trackParamsCommon, {
          word: loc.name,
        })
      )
    })

    // 选择城市
    componentStore.bus.on('selectCity', (info: CityInfo) => {
      console.log('====================================')
      console.log('selectCity', info)
      console.log('====================================')
      if (info.code === componentStore.city?.code) {
        componentStore.bus.emit('hideCountyLayoutLoading')
      }
      componentStore.$patch({
        selectedCityFromAddressPod: JSON.parse(JSON.stringify(info)) as CityInfo,
      })
      selectedCityLoc = info.loc // 位置都以gis给的文件为准
      if (info.code !== '000000') {
        flyTo(mapContext, gcj02ToBd09(selectedCityLoc), info.zoom)

        // 20250508 去掉展示城市边界
        // if (componentStore.viewMode === MapViewMode.CountyLayout) {
        //   // 县县通按默认处理
        //   flyTo(mapContext, gcj02ToBd09(selectedCityLoc), info.zoom)
        //   return
        // }
        // if (info.useDefaultZoom) {
        //   // 需要单独处理的地区
        //   flyTo(mapContext, gcj02ToBd09(selectedCityLoc), info.zoom)
        // } else {
        //   const infoName = info.simple!.split('_')[0].split('-').join('')
        //   // 使用gis给的location 用百度的会有误差
        //   mapContext
        //     .getAreaBoundary(infoName)
        //     .then((result: any) => {
        //       let resZoom = result.zoom
        //       switch (componentStore.dimension) {
        //         case '省':
        //           resZoom =
        //             result.zoom > DEFAULT_CITY_ZOOM_LEVEL ? DEFAULT_CITY_ZOOM_LEVEL : result.zoom
        //           break
        //         case '市':
        //           resZoom =
        //             result.zoom > DEFAULT_DISTRICT_ZOOM_LEVEL
        //               ? DEFAULT_DISTRICT_ZOOM_LEVEL
        //               : result.zoom
        //           break
        //         case '区县':
        //           resZoom =
        //             result.zoom > DEFAULT_DISTRICT_ZOOM_LEVEL
        //               ? result.zoom
        //               : DEFAULT_DISTRICT_ZOOM_LEVEL
        //           break

        //         default:
        //           break
        //       }
        //       if (info.useGeometricCenter) {
        //         // 需要单独处理的地区
        //         flyTo(mapContext, result.center, info.zoom)
        //       } else {
        //         flyTo(mapContext, gcj02ToBd09(selectedCityLoc!), resZoom)
        //       }
        //     })
        //     .catch(() => {
        //       console.log(
        //         '============// 有些信息查不到（eg.直辖县）走默认zoom========================'
        //       )
        //       // 有些信息查不到（eg.直辖县）走默认zoom
        //       flyTo(mapContext, gcj02ToBd09(selectedCityLoc!), info.zoom)
        //     })
        // }
      } else {
        mapInstance.value?.clearOverlays()
        flyTo(mapContext, gcj02ToBd09(selectedCityLoc!), state.mapSetting.zoom)
      }
    })

    // 选择城市心愿站
    componentStore.bus.on('selectCityWishPower', (info: CityInfo) => {
      if (info.code !== '000000') {
        return flyTo(mapContext, gcj02ToBd09(info.loc), state.mapSetting.infoZoomLevel)
      } else {
        flyTo(mapContext, gcj02ToBd09(info.loc), state.mapSetting.zoom)
      }
    })

    /**
     * 更新位置
     * @param loc 设置位置中心
     * @param refreshContent 更新中心位置是否需要刷新地图内容 默认true
     */
    const updateCenterLocation = async (loc: Point) => {
      if (window.updateCenterLocationDebounce) {
        window.updateCenterLocationDebounce.cancel()
        delete window.updateCenterLocationDebounce
      }
      window.updateCenterLocationDebounce = debounce(
        async () => {
          await componentStore.setCenter(loc)
          selectedCityLoc = null
          if (componentStore.centerLocation) {
            if (specialArea.indexOf(componentStore.centerLocation.province) > -1) {
              await componentStore.selectCityByArr([componentStore.centerLocation.province])
            } else if (componentStore.dimension) {
              if (componentStore.dimension == '区县') {
                await componentStore.selectCityByArr([
                  componentStore.centerLocation.province,
                  componentStore.centerLocation.city,
                  componentStore.centerLocation.district,
                ])
              } else {
                await componentStore.selectCityByArr([
                  componentStore.centerLocation.province,
                  componentStore.centerLocation.city,
                ])
              }
              componentStore.$patch({ dimension: '' })
            } else {
              const zoomLevel = mapContext.getZoom()
              if (componentStore.viewMode == MapViewMode.CountyLayout) {
                // 20250417 县县通只到市
                await componentStore.selectCityByArr([
                  componentStore.centerLocation.province,
                  componentStore.centerLocation.city,
                ])
              } else {
                if (zoomLevel > DEFAULT_DISTRICT_ZOOM_LEVEL) {
                  await componentStore.selectCityByArr([
                    componentStore.centerLocation.province,
                    componentStore.centerLocation.city,
                    componentStore.centerLocation.district,
                  ])
                } else {
                  await componentStore.selectCityByArr([
                    componentStore.centerLocation.province,
                    componentStore.centerLocation.city,
                  ])
                }
              }
            }
            if (componentStore.viewMode == MapViewMode.WishPower) {
              if (
                ['澳门特别行政区', '香港特别行政区'].indexOf(
                  componentStore.centerLocation.province
                ) > -1
              ) {
                await componentStore.selectCityWishPower(componentStore.centerLocation.province)
              } else {
                await componentStore.selectCityWishPower(componentStore.centerLocation.city)
              }
            }
          }
        },
        800,
        { leading: false }
      )
      window.updateCenterLocationDebounce()
    }

    const renderResourceOnMapReady = () => {
      getChargemapCDNurl(componentStore.dictionaries)
        .then((res: any) => {
          cdnUrlResProps.value = res
          // mapReadyRequestData(res)
          componentsConfig?.wishpower?.show && renderWishPower(res) // 提前获取心愿站数据
        })
        .finally(() => {
          emit('ready')
        })
    }

    // -------------------------- 事件 -----------------------
    const onMapReady = async (context: ChargeMapContext) => {
      // 请求cdn资源
      componentsConfig?.delay_render?.show
        ? setTimeout(() => {
            // 门店信发旧设备会渲染卡死，需要手动读取delay
            renderResourceOnMapReady()
          }, componentsConfig?.delay_render?.properties?.delay || 5000)
        : renderResourceOnMapReady()

      mapContext = context
      mapContextRef.value = context
      mapInstance.value = mapContextRef.value?.getMap()
      BMapGL.value = window.BMapGL
      if (componentStore.showSummary) {
        disableZoom()
        disableDragging()
      }
      // 自适应屏幕 将中国定位到地图中心
      const initCenterZoomInit = fitWindows(mapContext, [
        appStore.chinaBoundSW,
        appStore.chinaBoundNE,
      ])
      // console.log('====================================')
      // console.log('initCenterZoomInit', initCenterZoomInit)
      // console.log('====================================')
      initCenterZoomInit.zoom =
        window.client === 'pc' && initCenterZoomInit.zoom < 5 ? 5 : initCenterZoomInit.zoom // pc端小于5 出现赤道线
      // console.log('====================================')
      // console.log('initCenterZoomInit2', initCenterZoomInit)
      // console.log('====================================')
      setMapStyleByConfig(mapContext, componentsConfig) // 读取配置样式
      const initCenterZoom = OS.isNioBrowser
        ? {
            center: mapContext.getCenter(),
            zoom: mapContext.getZoom(),
          }
        : initCenterZoomInit
      state.mapSetting.center = initCenterZoom.center
      state.mapSetting.zoom = initCenterZoom.zoom
      state.mapSetting.minZoom = initCenterZoom.zoom
      mapContext.setZoom(initCenterZoom.zoom)
      mapContext.setMinZoom(initCenterZoom.zoom) // 设置地图允许的最小级别
      appStore.$patch({
        minZoomLevel: initCenterZoom.zoom,
      })
      mapContext.setMaxZoom(state.mapSetting.maxZoom)
      mapContext.getMap().setMapType((window as any).BMAP_NORMAL_MAP)
      mapContext.showMapText(false) // 默认不显示文字
      componentStore.$patch({ mapContext: context }) // 记录地图 给其他组件使用
      // 埋点：loading关闭时间
      const loadingEndTime = new Date().getTime()
      appStore.pageStartTime &&
        Tracker.track(
          'nsh5_loadingclose',
          Object.assign(appStore.trackParamsCommon, {
            timebetween: loadingEndTime - appStore.pageStartTime,
          })
        )
      context
        .getCurrentPosition(componentsConfig?.location?.properties?.option || {})
        .then(async (loc: Point) => {
          componentStore.setMyLocation(loc)
          componentStore.bus.emit('checkCanPreviewCity', loc)
        })
        .catch(async () => {
          componentStore.setMyLocation(DEFAULT_POSITION)
          componentStore.bus.emit('checkCanPreviewCity', null)
          appStore.$patch({
            locationSuccess: false,
          })
        })
    }

    const disableZoom = () => {
      if (client !== 'mobile') {
        const mapIns = mapContext?.getMap()
        mapIns?.disablePinchToZoom() // 禁用双指缩放地图
        mapIns?.disableScrollWheelZoom() // 禁止地图被鼠标滚轮缩放
      }
    }

    const enableZoom = () => {
      const mapIns = mapContext?.getMap()
      mapIns?.enablePinchToZoom() // 禁用双指缩放地图
      mapIns?.enableScrollWheelZoom() // 禁止地图被鼠标滚轮缩放
    }

    const disableDragging = () => {
      const mapIns = mapContext?.getMap()
      mapIns?.disableDragging()
    }

    const enableDragging = () => {
      const mapIns = mapContext?.getMap()
      mapIns?.enableDragging()
    }

    /**
     * 渲染全国和城市级别的hotmap
     */
    const renderWishPower = (cdnUrlRes: any) => {
      // 只请求数据，不画图层，减少首屏渲染压力
      // 打开心愿站会触发zoomEnd时间，在zoomEnd事件中会根据数据渲染图层
      componentStore.getWishPowerByCND(cdnUrlRes) // 获取心愿站资源
    }

    // 点击路线的时候打断首页的bling
    componentStore.bus.on('handleBlingClear', () => {
      if (state.zoomMode != ZoomMode.City) {
        mapContext.interruptBling()
      }
    })

    const sendRealTimeNum = (obj: any) => {
      // 实时换电
      if (Object.prototype.hasOwnProperty.call(obj, 'realtime_swap_order_num')) {
        if (componentsConfig?.data_default?.properties?.stop_flip) {
          // 取消数字跳动
          return
        }
        if (obj['realtime_swap_order_num'] && obj['realtime_swap_order_num'].num > 0) {
          // 换电数据变化
          // console.log('===============全国---换电数据变化=====================')
          // console.log(obj['realtime_swap_order_num'])
          // console.log('====================================')
          componentStore.$patch({ swapNum: obj['realtime_swap_order_num'].num })
        }
      }
      if (Object.prototype.hasOwnProperty.call(obj, 'realtime_charge_order_num')) {
        if (componentsConfig?.data_default?.properties?.stop_flip) {
          // 取消数字跳动
          return
        }
        if (obj['realtime_charge_order_num'] && obj['realtime_charge_order_num'].num > 0) {
          // 充电数据变化
          // console.log('===============全国---充电数据变化=====================')
          // console.log(obj['realtime_charge_order_num'])
          // console.log('====================================')
          componentStore.$patch({ chargeNum: obj['realtime_charge_order_num'].num })
        }
      }
      // 实时累计换电数量或实时换电站数量达成，触发运营位id
      if (Object.prototype.hasOwnProperty.call(obj, 'hit_act_id')) {
        if (obj['hit_act_id']) {
          // 触发运营位id
          // console.log('===============触发运营位id=====================')
          // console.log(obj['hit_act_id'])
          // console.log('====================================')
          componentStore.bus.emit('screenPopupShow', obj['hit_act_id'])
          // 触发数字动画背景
          // 触发数字动画背景
          setTimeout(() => {
            componentStore.bus.emit('numberAnimationShow', obj['hit_act_id'])
          }, componentsConfig?.number_animation?.properties?.delay_time || 1500);
        }
      }
      // 心愿站
      if (Object.prototype.hasOwnProperty.call(obj, 'wish_station_num')) {
        if (obj['wish_station_num']) {
          // 心愿站数据变化
          componentStore.bus.emit('updateWishStationNumMapping', obj['wish_station_num'])
        }
      }
      // 实时换电状态
      if (componentsConfig?.realtime_swap_status?.show) {
        if (Object.prototype.hasOwnProperty.call(obj, 'swap_status')) {
          if (
            obj['swap_status'] &&
            componentStore.viewMode === MapViewMode.Init &&
            state.zoomMode === ZoomMode.City
          ) {
            // 换电状态变化
            changeChargingStatus(mapContext, [
              {
                swapId: obj['swap_status']['resource_id'],
                swapStatus: obj['swap_status']['service_state'],
              },
            ])
          }
        }
      }
      // 换电完成bling效果
      if (componentsConfig?.finished_swap_status?.show) {
        // 判断换电站是不是被选上
        const selectedSwap =
          componentStore.filter.findIndex(
            (item) => item.key === 'recharge|ps' && item.filterState == FilterState.On
          ) !== -1
        if (Object.prototype.hasOwnProperty.call(obj, 'finished_swap_location')) {
          if (
            componentStore.showSummary ||
            (componentStore.viewMode == MapViewMode.Init && // 地图模式，心愿站和路径规划不监听
              state.zoomMode != ZoomMode.City && // 全国模式，路线和城市不监听
              obj['finished_swap_location'] &&
              route.name == 'home' && // 只有首页展示
              selectedSwap) // 筛选  有换电站情况下监听
          ) {
            // 换电完成Bling
            const locations = obj['finished_swap_location']
            const blingPoints = locations.map((location: string) => {
              const position = location.split(',') // gcj
              const point = gcj02ToBd09({
                lng: Number(position[0]),
                lat: Number(position[1]),
              })
              return point
            })
            mapContext?.addBlingAnimation(blingPoints, calcSize(mapContext.getZoom()).ratio)
          }
        }
      }
    }

    /**
     * vglMarker点击事件
     * @param marker
     */
    const onVglMarkerClick = (obj: { marker: Point }) => {
      const { marker } = obj
      switch (componentStore.viewMode) {
        case MapViewMode.WishPower:
          componentStore.bus.emit('wishIconRenderClickedVglMarker', marker)
          break

        default:
          break
      }
    }

    const onDragEnd = () => {
      if (
        componentStore.viewMode === MapViewMode.EnergyPlan ||
        state.zoomMode == ZoomMode.Country
      ) {
        return
      }
      updateCenterLocationInfo()
    }

    const onZoomEnd = async () => {
      const zoomLevel = mapContext.getZoom()
      console.log('====================================')
      console.log('onZoomEnd--zoomLevel', zoomLevel)
      console.log('====================================')
      state.zoomMode =
        zoomLevel > DEFAULT_CITY_ZOOM_LEVEL
          ? ZoomMode.City
          : zoomLevel > DEFAULT_COUNTRY_ZOOM_LEVEL
          ? ZoomMode.Route
          : ZoomMode.Country
      mapContext.getMap().setDisplayOptions({
        poiText: state.zoomMode != ZoomMode.Country,
        poiIcon: state.zoomMode != ZoomMode.Country,
        street: state.zoomMode != ZoomMode.Country,
      })
      // 缩放到最小回到中心
      if (zoomLevel - 0.2 <= state.mapSetting.minZoom) {
        flyTo(mapContext, state.mapSetting.center)
      }
      updateCenterLocationInfo()
      componentStore.$patch({
        showPsDistrictHousingCoveredRate: zoomLevel <= DEFAULT_DISTRICT_ZOOM_LEVEL,
      })
    }

    /**
     * 根据屏幕中心位置结合zoomLevel获取中心点信息
     */
    const updateCenterLocationInfo = async () => {
      if (state.zoomMode != ZoomMode.City && componentStore.viewMode == MapViewMode.WishPower) {
        // 心愿站-非城市情况下更新为全国
        componentStore.selectCityWishPower('全国')
      }
      // 按条件更新位置
      switch (state.zoomMode) {
        case ZoomMode.Country:
          componentStore.$patch({ dimension: '' }) // 回到全国需要清空数据看板的选择
          selectedCityLoc = null
          componentStore.selectCityByArr(['全国'])
          break
        case ZoomMode.Route:
          await componentStore.setCenter(selectedCityLoc || mapContext.getCenter())
          selectedCityLoc = null
          componentStore.centerLocation &&
            componentStore.selectCityByArr([componentStore.centerLocation.province])
          componentStore.$patch({ dimension: '' }) // 修复选择选择省后再拖动无反应
          break
        case ZoomMode.City:
          updateCenterLocation(selectedCityLoc || mapContext.getCenter())
          break

        default:
          break
      }
    }

    watch(
      () => componentStore.showSummary,
      () => {
        if (componentStore.showSummary) {
          disableZoom()
          disableDragging()
        } else {
          enableZoom()
          enableDragging()
        }
      }
    )

    onUnmounted(() => {
      componentStore.bus.off('*')
    })

    // ********************************************
    /**
     * 不同状态下点击icon触发不同组件的icon点击事件
     * @param marker
     */
    const onChargeMapMarkerClick = (marker: Marker) => {
      switch (componentStore.viewMode) {
        case MapViewMode.Init:
          componentStore.bus.emit('iconRenderClickedMarker', marker)
          break
        case MapViewMode.EnergyPlan:
          componentStore.bus.emit('energyPlansClickedMarker', marker)
          break
        case MapViewMode.RecommendRouteDetail:
          componentStore.bus.emit('recommendRouteDetailClickedMarker', marker)
          break
        case MapViewMode.NewStation:
          componentStore.bus.emit('newStationClickedMarker', marker)
          break
        case MapViewMode.CountyLayout:
          componentStore.bus.emit('countyLayoutClickedMarker', marker)
          break

        default:
          break
      }
    }

    componentStore.bus.on('goToDestination', (params: { dest: BubbleDestPoint }) => {
      componentStore.setCachePosition()
      const { dest } = params
      const o: Point = {
        lng: componentStore.myLocation?.lng || appStore.center.lng,
        lat: componentStore.myLocation?.lat || appStore.center.lat,
      }
      const d: Point = {
        lng: dest.lng,
        lat: dest.lat,
      }
      const origName = encodeURIComponent(componentStore.myLocation?.name || '我的位置')
      const destName = encodeURIComponent(dest.name)
      router.push(
        `/journey/plan?orig=${o.lng},${o.lat}&dest=${d.lng},${d.lat}&origName=${origName}&destName=${destName}`
      )
      // 埋点-城市地图页面地图到这里去
      Tracker.track(
        'nsh5_citymaplineclick',
        Object.assign(appStore.trackParamsCommon, {
          poiaddress: dest?.address,
          poiname: dest?.name,
        })
      )
    })

    /**
     * 20240523 - 新增导航跳转
     * 1、其他环境点击点位，打开百度地图导航
     * 2、微信小程序里，点击点位，调用universal bridge的openMap打开资源点
     */
    const getNavigationPageUrl = async (params: { dest: BubbleDestPoint }) => {
      componentStore.setCachePosition()
      const { dest } = params
      const o: Point = {
        lng: componentStore.myLocation?.lng || appStore.center.lng,
        lat: componentStore.myLocation?.lat || appStore.center.lat,
      }
      const d: Point = {
        lng: dest.lng,
        lat: dest.lat,
      }
      const destName = encodeURIComponent(dest.name)
      if (client === 'mobile') {
        return `http://api.map.baidu.com/direction?origin=${o.lat}%2C${o.lng}&destination=${d.lat}%2C${d.lng}|name:${destName}&mode=driving&region=dd&coord_type=gcj02&mode=driving&output=html&src=webapp.nio.chargemap`
      } else {
        return `http://api.map.baidu.com/direction?origin=${o.lat}%2C${o.lng}&destination=latlng:${d.lat}%2C${d.lng}|name:${destName}&mode=driving&region=dd&coord_type=gcj02&mode=driving&output=html&src=webapp.nio.chargemap`
      }
    }
    componentStore.bus.on('goToNavigateAddress', async (params: { dest: BubbleDestPoint }) => {
      try {
        // 不在微信小程序中
        if (!isInMiniProgram()) {
          const pageUrl = await getNavigationPageUrl(params)
          console.log('==================goToNavigateAddress====================', pageUrl)
          await nioJsBridge.openURL({
            data: {
              url: pageUrl,
            },
          })
        } else {
          await nioJsBridge.openMap({
            data: {
              latitude: params.dest.lat.toString(),
              longitude: params.dest.lng.toString(),
              addressName: encodeURIComponent(params.dest.name),
            },
          })
        }
      } catch (err) {
        console.error(`Error: ${err}`)
      }
    })

    const displayInformationByConfig = () => {
      if (componentsConfig?.data_default?.show) {
        componentStore.$patch({
          showHandleBox: false,
        })
        showSummary() // 入口就是首页，显示summary
      } else {
        componentStore.selectCityByArr(['全国']) // 若关闭首页需要请求一遍全国数据
        componentStore.$patch({
          showHandleBox: true,
        })
        hideSummary()
      }
    }

    componentStore.bus.on('displayInformationByConfig', displayInformationByConfig)

    router.afterEach(async (to, from) => {
      if (!from.name) {
        if (to.name === 'home') {
          displayInformationByConfig()
        } else {
          if (to.name === 'pin' || to.name === 'resource') {
            componentStore.$patch({
              showHandleBox: true,
            })
          }
          if (to.name === 'newStation') {
            componentStore.$patch({ viewMode: MapViewMode.NewStation })
            componentStore.$patch({
              showHandleBox: false,
            })
          }
          if (to.name === 'countyLayout') {
            componentStore.$patch({ viewMode: MapViewMode.CountyLayout })
            componentStore.$patch({
              showHandleBox: false,
            })
          }
          hideSummary() // 入口不是首页，隐藏summary
        }
        return
      }
      if (to.name === 'home') {
        window.backHistoryNum = 0
        componentStore.$patch({ viewMode: MapViewMode.Init })
        componentStore.$patch({
          showHandleBox: true,
        })
        if (from.name === 'wishpower_index') {
          mapContext.setMaxZoom(state.mapSetting.maxZoom) // 因为进入心愿站模式，所以需要重新设置最大缩放级别
        }
        if (from.name === 'newStation') {
          // 因为进入新站上新，回到初始中心及zoom
          flyTo(mapContext, state.mapSetting.center, state.mapSetting.zoom)
        }
        if (from.name === 'countyLayout') {
          // 因为进入新站上新，回到初始中心及zoom
          flyTo(mapContext, state.mapSetting.center, state.mapSetting.zoom)
        }
        return
      }
      if (to.name === 'recommend') {
        componentStore.$patch({ viewMode: MapViewMode.Recommend })
        componentStore.$patch({
          showHandleBox: false,
        })
        // 回到初始中心及zoom
        flyTo(mapContext, state.mapSetting.center, state.mapSetting.zoom)
        return
      }
      if (to.name === 'detail') {
        componentStore.$patch({ viewMode: MapViewMode.RecommendRouteDetail })
        componentStore.$patch({
          showHandleBox: false,
        })
        return
      }
      if (from.name === 'countyLayout' || to.name === 'data_chooseCity') {
        // 从县县通打开城市选择器不需要改变viewMode状态
        return
      }
      if (to.name === 'data_summary' || to.name === 'data_chooseCity') {
        componentStore.$patch({ viewMode: MapViewMode.Init })
        componentStore.$patch({
          showHandleBox: false,
        })
        return
      }
      if (
        (from.name === 'data_summary' || from.name === 'data_chooseCity') &&
        (to.name === 'resource' || to.name === 'pin')
      ) {
        // 从数据列表直接点击地图上的icon或pion
        componentStore.$patch({ viewMode: MapViewMode.Init })
        componentStore.$patch({
          showHandleBox: true,
        })
        return
      }
      if (to.name === 'plan') {
        componentStore.$patch({ viewMode: MapViewMode.EnergyPlan })
        componentStore.$patch({
          showHandleBox: false,
        })
        return
      }
      if (from.name === 'plan' && (to.name === 'pin' || to.name === 'resource')) {
        componentStore.$patch({ viewMode: MapViewMode.Init })
        componentStore.$patch({
          showHandleBox: true,
        })
        return
      }
      if (to.name === 'wishpower_index' || to.name === 'wishpower_chooseCity') {
        if (componentStore.showHandleBox) {
          // 回到初始中心及zoom
          flyTo(mapContext, state.mapSetting.center, state.mapSetting.zoom)
        }
        componentStore.$patch({ viewMode: MapViewMode.WishPower })
        componentStore.$patch({
          showHandleBox: false,
        })
        return
      }
      // 操作栏-新增附近资源-隐藏操作栏
      if (route.name == 'around_resource') {
        componentStore.$patch({ viewMode: MapViewMode.Init })
        componentStore.$patch({
          showHandleBox: false,
        })
        return
      }
      if (from.name === 'around_resource') {
        componentStore.$patch({
          showHandleBox: true,
        })
        return
      }
      if (to.name === 'newStation') {
        componentStore.$patch({ viewMode: MapViewMode.NewStation })
        componentStore.$patch({
          showHandleBox: false,
        })
        if (from.name === 'home') {
          // 回到初始中心及zoom
          flyTo(mapContext, state.mapSetting.center, state.mapSetting.zoom)
        }
        return
      }
      if (to.name === 'countyLayout') {
        componentStore.$patch({ viewMode: MapViewMode.CountyLayout })
        componentStore.$patch({
          showHandleBox: false,
        })
        // 县县通没有全国模式了   所以不需要回到回到初始中心及zoom
        // if (from.name === 'home') {
        // 回到初始中心及zoom
        // flyTo(mapContext, state.mapSetting.center, state.mapSetting.zoom)
        // }
        return
      }
    })

    const compsRef = ref<JSX.Element[]>([])
    /**
     * 配置渲染组件
     */
    const renderComps = (compList: any) => {
      compList.forEach((element: any) => {
        const propsInsert = {} as any
        element.props.mapContextRef && (propsInsert.mapContextRef = mapContextRef.value)
        element.props.mapInstance && (propsInsert.mapInstance = mapInstance.value)
        element.props.componentStore && (propsInsert.componentStore = componentStore)
        element.props.cdnUrl && (propsInsert.cdnUrl = cdnUrlResProps.value)
        element.props.BMapGL && (propsInsert.BMapGL = BMapGL.value)
        element.props.settings && (propsInsert.settings = state.mapSetting)
        element.props.xinfaView && (propsInsert.settings = xinfaView)
        compsRef.value.push(<>{element.comp && h(resolveComponent(element.comp), propsInsert)}</>)
      })
    }

    expose({
      renderComps,
    })

    return () => {
      return (
        <>
          {router && route && (
            // 模块里面很多都是通过读取router判断的，所以需要先保证router创建完成
            <>
              {/* 可灵活配置的模块 */}
              {compsRef.value.map((comp) => comp)}
            </>
          )}

          {/* 地图模块，常驻 */}
          <div
            class={[
              styles.mapDiv,
              client === 'mobile' && componentsConfig?.data_default?.show && styles.mobileMap,
            ]}
          >
            <ChargerMap
              settings={state.mapSetting}
              onReady={onMapReady}
              onMarkerClick={onChargeMapMarkerClick}
              onVglMarkerClick={onVglMarkerClick}
              onDragend={onDragEnd}
              onZoomend={onZoomEnd}
            />
          </div>

          {/* 以下为常驻模块，默认存在不参与配置 */}
          <BackListRecord componentStore={componentStore}></BackListRecord>
          <AutoRefresh
            mapContextRef={mapContextRef.value}
            mapInstance={mapInstance.value}
            componentStore={componentStore}
            mapSetting={state.mapSetting}
            onBackHome={displayInformationByConfig}
          ></AutoRefresh>
          <WsService onReceiveChargeMapMessage={sendRealTimeNum}></WsService>
          <PopupDrag
            mapContextRef={mapContextRef.value}
            mapInstance={mapInstance.value}
            componentStore={componentStore}
          ></PopupDrag>
        </>
      )
    }
  },
})
